import { BusinessObjectBase } from './base';
import { processUserInterfacePropertySchema } from '@schemas/business-objects/process-user-interface-property';
import type { ProcessDataType } from './process-data-type';
import type { ProcessUserInterface } from './process-user-interface';
import type { Id } from '../base-types';

export interface ProcessUserInterfacePropertyData {
  readonly id: Id;
  readonly name?: string;
  readonly isArray?: boolean;
  readonly isInput?: boolean;
  readonly isOutput?: boolean;
  readonly required?: boolean;
  readonly description?: string;
  readonly sequence?: number;
  readonly userInterfaceId: Id;
  readonly userInterface?: ProcessUserInterface;
  readonly typeId: Id;
  readonly type?: ProcessDataType;
}

export interface ProcessUserInterfaceProperty extends Readonly<ProcessUserInterfacePropertyData> {}
type ProcessUserInterfacePropertyCreateWithChildrenData =
  Partial<ProcessUserInterfacePropertyData>;

export class ProcessUserInterfaceProperty extends BusinessObjectBase {
  static readonly entityName = 'ProcessUserInterfaceProperty' as const;
  static readonly plural = 'process-user-interface-properties';
  static schema = processUserInterfacePropertySchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ProcessUserInterfaceProperty[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ProcessUserInterfaceProperty(data));
  }

  static async findOne(
    filter?: any,
  ): Promise<ProcessUserInterfaceProperty | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ProcessUserInterfaceProperty(data) : null;
  }

  static async findById(
    id: Id,
    filter?: any,
  ): Promise<ProcessUserInterfaceProperty> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ProcessUserInterfaceProperty(data);
  }

  static async create(
    data: Omit<ProcessUserInterfacePropertyData, 'id'>,
  ): Promise<ProcessUserInterfaceProperty> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ProcessUserInterfaceProperty(result);
  }

  static async createMany(
    data: Omit<ProcessUserInterfacePropertyData, 'id'>[],
  ): Promise<ProcessUserInterfaceProperty[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessUserInterfaceProperty(item));
  }

  static async createWithChildren(
    data: ProcessUserInterfacePropertyCreateWithChildrenData,
  ): Promise<ProcessUserInterfaceProperty> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new ProcessUserInterfaceProperty(result);
  }

  static async createManyWithChildren(
    data: ProcessUserInterfacePropertyCreateWithChildrenData[],
  ): Promise<ProcessUserInterfaceProperty[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessUserInterfaceProperty(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ProcessUserInterfacePropertyData>,
  ): Promise<ProcessUserInterfaceProperty> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ProcessUserInterfaceProperty(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
